import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreatePetOwner } from '../../../hooks/patients/useCreatePetOwner';
import { useUpdatePetOwner } from '../../../hooks/patients/useUpdatePetOwner';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';

export default function PetOwnerForm({ successCallback = null, petOwner = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const { role, isAuthenticated } = useAuth();

    const [firstName, setFirstName] = React.useState(petOwner ? petOwner.first_name : "");
    const [lastName, setLastName] = React.useState(petOwner ? petOwner.last_name : "");
    const [email, setEmail] = React.useState(petOwner ? petOwner.email : "");
    const [contactNumber, setContactNumber] = React.useState(petOwner ? petOwner.contact_number : "");
    const [address, setAddress] = React.useState(petOwner ? petOwner.address : "");

    const { create } = useCreatePetOwner(successCallback);
    const { update } = useUpdatePetOwner(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setFirstName(petOwner ? petOwner.first_name : "");
        setLastName(petOwner ? petOwner.last_name : "");
        setEmail(petOwner ? petOwner.email : "");
        setContactNumber(petOwner ? petOwner.contact_number : "");
        setAddress(petOwner ? petOwner.address : "");
    }, [petOwner, open]);

    const handleSave = async () => {
        if (!firstName.trim()) {
            showSnackbar(t('pet_owners.messages.first_name_required'), "warning");
            return;
        }
        if (!lastName.trim()) {
            showSnackbar(t('pet_owners.messages.last_name_required'), "warning");
            return;
        }
        if (!email.trim()) {
            showSnackbar(t('pet_owners.messages.email_required'), "warning");
            return;
        }
        if (!contactNumber.trim()) {
            showSnackbar(t('pet_owners.messages.contact_number_required'), "warning");
            return;
        }
        if (!contactNumber.trim()) {
            showSnackbar(t('pet_owners.messages.address_required'), "warning");
            return;
        }

        try 
        {
            if (mode === "create") {
                await create({ 
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    contact_number: contactNumber,
                    address: address 
                });
                showSnackbar(t('pet_owners.messages.create_success'), "success");
            } else if (mode === "edit" && petOwner) {
                await update(petOwner.id, { 
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    contact_number: contactNumber,
                    address: address 
                 });
                showSnackbar(t('pet_owners.messages.update_success'), "success");
            }
            setFirstName("");   // clear field after save
            setLastName("");
            setEmail("");
            setContactNumber("");
            setAddress("");
            onClose();     // close dialog
        } catch (err) {
            console.log("err.response", err.response);
            if (err.response?.status === 409) {
                showSnackbar(t('pet_owners.messages.email_duplicate'), "error");
            } else {
                showSnackbar(t('pet_owners.messages.save_fail'), "error");
            }
        }

    };

    const handleCancel = () => {
        setFirstName("");   // clear field on cancel
        setLastName("");
        setEmail("");
        setContactNumber("");
        setAddress("");
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        {role === 'guest' ?
        <DialogTitle>Enter Your Details</DialogTitle>
        :
        <DialogTitle>{mode === "create" ? "Add Pet Owner" : "Edit Pet Owner"}</DialogTitle>
        }

        <DialogContent dividers>
            <Stack spacing={1}>
            <TextField
                label="First Name"
                variant="outlined"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
            />
            <TextField
                label="Last Name"
                variant="outlined"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
            />
            <TextField
                label="E-mail"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
            />
            <TextField
                label="Contact Number"
                variant="outlined"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                fullWidth
            />
            <TextField
                label="Address"
                variant="outlined"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                multiline
                rows={2}
            />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                {role === 'guest' ? "Next" : "Save" }
            </Button>
            {role === 'guest' ? null : <Button onClick={handleCancel}>Cancel</Button>}
        </DialogActions>
        </Dialog>
        </>
    );
}